/**
 * 匿名の進捗記録 `zp.progress` の読み書き（TK-033、FR-019、`05-database-schema.md` §4、ADR-017）。
 *
 * 保存するのはシリーズごとのクリア状態（`cleared`）・クリア済み問題数（`done`）・
 * 再開ポイント URL（`resume`）・最終更新時刻（`at`）のみで、個人を識別しうる情報は
 * 一切含まない（NFR-002）。盤面の途中状態は保存しない。
 *
 * 取り扱い規則は `zp.age` と同じ（`05` §4.2）:
 * - 破損（JSON パース失敗・`v` 不一致・型不整合）はレコードを削除し、未プレイとして扱う
 * - `localStorage` が使えない場合はメモリ上のレコードにフォールバックする（当該セッション
 *   中のみ有効）。エラーは表示しない
 *
 * 更新の判断はすべて純関数 `applyStageClear` に置き、ストレージへの接続は
 * `createProgressStore` が受け持つ。
 */

import type { WebStorage } from './storage';

/** レコードのキー。 */
export const PROGRESS_KEY = 'zp.progress';

/** レコードバージョン。不一致は破棄する（前方・後方互換を取らない。`05` §7）。 */
export const PROGRESS_RECORD_VERSION = 1;

/** シリーズ 1 つぶんの進捗。 */
export interface SeriesProgress {
  /** シリーズ末尾までクリアしたか。 */
  readonly cleared: boolean;
  /** クリア済みの問題数（表示用）。 */
  readonly done: number;
  /** 再開ポイント（次に遊ぶステージの URL）。クリア済みシリーズでは持たない。 */
  readonly resume?: string;
  /** 最終更新時刻（epoch ミリ秒）。 */
  readonly at: number;
}

/** シリーズ連番 → 進捗。レコードの `series` フィールドの中身。 */
export type ProgressMap = Readonly<Record<string, SeriesProgress>>;

/** ステージクリア確定時にゲーム側から渡される情報（埋め込みステージ設定由来。`05` §3）。 */
export interface StageClearInfo {
  /** シリーズのフォルダ連番（`001` 等）。 */
  readonly series: string;
  /** シリーズ内の位置（1 始まり）。 */
  readonly stageNo: number;
  /** シリーズの問題数。 */
  readonly stageCount: number;
  /** 次ステージの URL。シリーズ末尾では持たない。 */
  readonly next?: string;
  /** シリーズ末尾か。 */
  readonly isLast: boolean;
}

/**
 * クリア 1 回ぶんの更新規則（`05` §4.1）。
 *
 * - `done` を進め、`resume` を `next` の URL に更新する
 * - シリーズ末尾（`isLast`）のクリアで `cleared: true` にし、`resume` を削除する
 *
 * ブックマークから過去のステージを解き直した場合（`stageNo` が既存の `done` より小さい）は
 * 前線を巻き戻さない — `done` は減らず、`resume` も後退しない。クリア済みシリーズの
 * 解き直しでも `cleared` は立ったまま、`resume` は復活しない。
 */
export function applyStageClear(map: ProgressMap, info: StageClearInfo, now: number): ProgressMap {
  const previous = map[info.series];
  if (info.isLast) {
    return { ...map, [info.series]: { cleared: true, done: info.stageCount, at: now } };
  }

  const prevDone = previous?.done ?? 0;
  const done = Math.max(prevDone, info.stageNo);
  const advanced = info.stageNo >= prevDone;
  const cleared = previous?.cleared ?? false;
  const resume = cleared ? undefined : advanced ? info.next : previous?.resume;
  return {
    ...map,
    [info.series]: {
      cleared,
      done,
      ...(resume !== undefined ? { resume } : {}),
      at: now,
    },
  };
}

/** 進捗の読み書きハンドル。 */
export interface ProgressStore {
  /** 現在の進捗。レコードが無い・壊れている・読めない場合は空。 */
  readonly read: () => ProgressMap;
  /** ステージクリアの確定を記録する。保存できない環境ではメモリ上にのみ残す。 */
  readonly recordClear: (info: StageClearInfo, now: number) => void;
}

/**
 * 進捗ストアを作る。
 *
 * @param storage `safeStorage()` の結果。`null`（利用不可）ならメモリフォールバックで動く
 */
export function createProgressStore(storage: WebStorage | null): ProgressStore {
  /** 書き込みが失敗したセッションでの控え。読み取りはストレージ優先で、これは最後の拠り所。 */
  let memory: ProgressMap | null = null;

  const read = (): ProgressMap => {
    if (storage === null) return memory ?? {};
    let raw: string | null;
    try {
      raw = storage.getItem(PROGRESS_KEY);
    } catch {
      return memory ?? {};
    }
    if (raw === null) return memory ?? {};
    const map = parseRecord(raw);
    if (map === null) {
      // 破損は破棄して未プレイとして扱う（FR-019）。エラーは表示しない。
      try {
        storage.removeItem(PROGRESS_KEY);
      } catch {
        /* 破棄すら失敗する環境では読み取りも失敗して未プレイになるため実害がない */
      }
      return memory ?? {};
    }
    return map;
  };

  return {
    read,
    recordClear: (info, now) => {
      const next = applyStageClear(read(), info, now);
      memory = next;
      if (storage === null) return;
      try {
        storage.setItem(PROGRESS_KEY, JSON.stringify({ v: PROGRESS_RECORD_VERSION, series: next }));
      } catch {
        /* 保存できない環境ではメモリ上の控えが当該セッションを支える */
      }
    },
  };
}

/** JSON を検証つきで読む。構造が仕様と一致しないものはすべて `null`（= 破損）。 */
function parseRecord(raw: string): ProgressMap | null {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  if (record.v !== PROGRESS_RECORD_VERSION) return null;
  if (typeof record.series !== 'object' || record.series === null) return null;

  const map: Record<string, SeriesProgress> = {};
  for (const [series, entryValue] of Object.entries(record.series as Record<string, unknown>)) {
    if (typeof entryValue !== 'object' || entryValue === null) return null;
    const entry = entryValue as Record<string, unknown>;
    if (typeof entry.cleared !== 'boolean') return null;
    if (typeof entry.done !== 'number' || !Number.isInteger(entry.done) || entry.done < 0)
      return null;
    if (entry.resume !== undefined && typeof entry.resume !== 'string') return null;
    if (typeof entry.at !== 'number' || !Number.isFinite(entry.at)) return null;
    map[series] = {
      cleared: entry.cleared,
      done: entry.done,
      ...(entry.resume !== undefined ? { resume: entry.resume } : {}),
      at: entry.at,
    };
  }
  return map;
}
